const MAX_ID_ATTEMPTS = 1000

function failure(current, error) {
  return { ok: false, error, featureCollection: current, addedFeatures: [] }
}

function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value)
}

function isPosition(value) {
  return Array.isArray(value) && value.length >= 2 && value.every(Number.isFinite)
}

function isPositionArray(value, minimumLength = 1) {
  return Array.isArray(value) && value.length >= minimumLength && value.every(isPosition)
}

function positionsEqual(first, last) {
  return first.length === last.length && first.every((ordinate, index) => ordinate === last[index])
}

function isLinearRing(value) {
  return isPositionArray(value, 4) && positionsEqual(value[0], value[value.length - 1])
}

function isGeometry(geometry) {
  if (!isObject(geometry) || typeof geometry.type !== "string") return false

  switch (geometry.type) {
    case "Point":
      return isPosition(geometry.coordinates)
    case "MultiPoint":
      return isPositionArray(geometry.coordinates)
    case "LineString":
      return isPositionArray(geometry.coordinates, 2)
    case "MultiLineString":
      return (
        Array.isArray(geometry.coordinates) &&
        geometry.coordinates.length > 0 &&
        geometry.coordinates.every((line) => isPositionArray(line, 2))
      )
    case "Polygon":
      return (
        Array.isArray(geometry.coordinates) &&
        geometry.coordinates.length > 0 &&
        geometry.coordinates.every(isLinearRing)
      )
    case "MultiPolygon":
      return (
        Array.isArray(geometry.coordinates) &&
        geometry.coordinates.length > 0 &&
        geometry.coordinates.every(
          (polygon) => Array.isArray(polygon) && polygon.length > 0 && polygon.every(isLinearRing),
        )
      )
    case "GeometryCollection":
      return Array.isArray(geometry.geometries) && geometry.geometries.every(isGeometry)
    default:
      return false
  }
}

function isFeature(feature) {
  return (
    isObject(feature) &&
    feature.type === "Feature" &&
    (feature.properties === null || isObject(feature.properties)) &&
    (feature.geometry === null || isGeometry(feature.geometry))
  )
}

function isFeatureCollection(collection) {
  return (
    isObject(collection) &&
    collection.type === "FeatureCollection" &&
    Array.isArray(collection.features) &&
    collection.features.every(isFeature)
  )
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function defaultIdFactory() {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
}

function effectiveId(feature) {
  return feature?.id ?? feature?.properties?.id
}

function idKey(id) {
  return String(id ?? "")
}

export function appendFeatureCollection(current, incoming, options = {}) {
  if (!isFeatureCollection(current)) return failure(current, "Invalid current FeatureCollection.")
  if (!Array.isArray(incoming) || !incoming.every(isFeature)) {
    return failure(current, "Invalid incoming Feature collection.")
  }

  const featureCap = options.featureCap
  if (!Number.isInteger(featureCap) || featureCap < 0) {
    return failure(current, "Invalid feature cap.")
  }

  const availableSlots = Math.max(0, featureCap - current.features.length)
  if (current.features.length + incoming.length > featureCap) {
    const noun = availableSlots === 1 ? "slot" : "slots"
    return failure(
      current,
      `Cannot add ${incoming.length} features; only ${availableSlots} feature ${noun} remain (cap ${featureCap}).`,
    )
  }

  if (incoming.length === 0) {
    return { ok: true, featureCollection: current, addedFeatures: [] }
  }

  const idFactory = options.idFactory ?? defaultIdFactory
  if (options.regenerateIds && typeof idFactory !== "function") {
    return failure(current, "Invalid ID factory.")
  }

  let currentClone
  let addedFeatures
  try {
    currentClone = clone(current)
    addedFeatures = clone(incoming)
  } catch {
    return failure(current, "Current or incoming Feature collection could not be cloned.")
  }

  if (!isFeatureCollection(currentClone) || !Array.isArray(addedFeatures) || !addedFeatures.every(isFeature)) {
    return failure(current, "Current or incoming Feature collection could not be cloned safely.")
  }

  if (options.regenerateIds) {
    const usedIds = new Set(currentClone.features.map((feature) => idKey(effectiveId(feature))))
    try {
      for (const feature of addedFeatures) {
        let freshId
        let attempts = 0
        do {
          if (attempts++ >= MAX_ID_ATTEMPTS) {
            return failure(current, "Could not generate a unique feature ID.")
          }
          freshId = idFactory()
        } while (freshId == null || freshId === "" || usedIds.has(idKey(freshId)))

        feature.id = freshId
        usedIds.add(idKey(freshId))
      }
    } catch (error) {
      return failure(current, `Could not generate a unique feature ID: ${error?.message || "unknown error"}`)
    }
  }

  try {
    currentClone.features.push(...addedFeatures)
    if (!isFeatureCollection(currentClone)) {
      return failure(current, "Current or incoming Feature collection could not be cloned safely.")
    }
  } catch {
    return failure(current, "Current or incoming Feature collection could not be assembled safely.")
  }

  return { ok: true, featureCollection: currentClone, addedFeatures }
}
